import { ReactElement, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { RaptorTheme, mainColors } from '../../../../../styling/theme';
import GridItem from '../../../../layout/GridComponents/GridItem';
import GenericGaugeChart from '../../../../charts/GenericGaugeChart';
import { useWindowSize } from 'react-use';
import { percentageToTwoDecimalPlaces } from '../../../../../utilities/numberFormatters';
import { Typography } from '@mui/material';
import usePngFromComponent from '../../../../../hooks/usePngFromComponent';
import { useDispatch, useSelector } from 'react-redux';
import { addComponentToPdfExport } from '../../../../../redux/fileExport/actions';
import { PdfComponentType } from '../../../../../types/redux/pdfExports/FileExportsStore';
import { activeSectionSelector } from '../../../../../redux/pages/selectors';
import { getEntityLabelForSection } from '../../../../../utilities/entityLabel';

const useMancoSummaryContainerStyles = makeStyles((theme: RaptorTheme) => ({
  regulationContainerRoot: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '0.62rem 1.88rem',
    paddingRight: 0,
    transition: 'all .3s',
    height: '100%',
    width: '100%',
    zIndex: 1,
    position: 'relative',
    cursor: 'pointer',
  },
  textSection: {
    display: 'flex',
    borderRadius: 4,
    flexDirection: 'column',
    justifyContent: 'space-around',
    width: '20%',
  },
  subTitle: {
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.12rem',
    },
  },
  subTitle_info: {
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.12rem',
    },
    fontSize: '1.50rem',
    fontWeight: 'bold',
    marginBottom: '0.94rem',
    color: theme.palette.primary.main,
  },
  pieSection: {
    cursor: 'pointer',
    display: 'flex',
    borderRadius: 4,
    width: '100%',
    justifyContent: 'space-around',
  },
}));

interface MancoSummaryProps {
  data: any;
}

function getMancoLevelData(data: any): any {
  if (!data.data.length) return undefined;
  if (!('manco' in data.data[0])) return undefined;
  return data.data[0].manco;
}

const getGuageHeight = (width: number) => {
  const calcHeight = width / 6 - 100;
  if (calcHeight < 100) {
    return 100;
  } else if (calcHeight > 250) {
    return 250;
  } else {
    return calcHeight;
  }
};

function MancoSummaryContainer({ data }: MancoSummaryProps): ReactElement {
  const classes = useMancoSummaryContainerStyles();
  const entityLabel = getEntityLabelForSection(
    useSelector(activeSectionSelector),
  );
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const mancoData = getMancoLevelData(data);

  const { ref, handleDownload } = usePngFromComponent();

  useEffect(() => {
    dispatch(
      addComponentToPdfExport({
        identifier: 'manco-report-delivery-oversight-summary',
        handler: handleDownload,
        type: PdfComponentType.GENERAL_COMPONENT_IMAGE,
        title: '',
        specifiedWidth: 12,
        dontMoveToNewPage: true,
        specificHeight: 50,
        groupOrder: 1,
        groupName: 'manco-report-delivery-oversight',
      }),
    );
  }, [data, ref]);

  return (
    <GridItem
      card
      key="manco-overview"
      item
      cardStyle={{ maxHeight: '25.00rem' }}
      xs={12}
    >
      <Typography
        variant="h1"
        style={{
          borderBottom: '0.62rem',
          marginBottom: '2.50rem',
          marginLeft: '1.25rem',
          marginTop: '1.25rem',
        }}
      >
        {`${entityLabel} Summary`}
      </Typography>

      <div className={classes.pieSection} ref={ref}>
        <GenericGaugeChart
          width={width / 6}
          height={getGuageHeight(width)}
          fillValue={mancoData ? mancoData['complete_reports'] : 0}
          fullValue={mancoData ? mancoData['total_reports'] : 0}
          title={'Complete Reports'}
          chartColor={mainColors.Pass_darker}
          centerText={
            mancoData
              ? percentageToTwoDecimalPlaces(
                  mancoData['complete_reports'] / mancoData['total_reports'],
                )
              : '0.00%'
          }
          startLabel="0%"
          endLabel="100%"
          textBelow={
            mancoData
              ? `${mancoData['complete_reports']}/${mancoData['total_reports']} Reports`
              : ''
          }
        />
        <GenericGaugeChart
          width={width / 6}
          height={getGuageHeight(width)}
          fillValue={mancoData ? mancoData['processing_reports'] : 0}
          fullValue={mancoData ? mancoData['total_reports'] : 0}
          title={'Currently Processing'}
          chartColor={mainColors.mainBlue}
          centerText={
            mancoData
              ? percentageToTwoDecimalPlaces(
                  mancoData['processing_reports'] / mancoData['total_reports'],
                )
              : '0.00%'
          }
          startLabel="0%"
          endLabel="100%"
          textBelow={
            mancoData
              ? `${mancoData['processing_reports']}/${mancoData['total_reports']} Reports`
              : ''
          }
        />
        <GenericGaugeChart
          width={width / 6}
          height={getGuageHeight(width)}
          fillValue={mancoData ? mancoData['analyst_input_reports'] : 0}
          fullValue={mancoData ? mancoData['total_reports'] : 0}
          title={'Analyst Input Required'}
          chartColor={mainColors.actionRequiredYellow}
          centerText={
            mancoData
              ? percentageToTwoDecimalPlaces(
                  mancoData['analyst_input_reports'] /
                    mancoData['total_reports'],
                )
              : '0.00%'
          }
          startLabel="0%"
          endLabel="100%"
          textBelow={
            mancoData
              ? `${mancoData['analyst_input_reports']}/${mancoData['total_reports']} Reports`
              : ''
          }
        />
        <GenericGaugeChart
          width={width / 6}
          height={getGuageHeight(width)}
          fillValue={mancoData ? mancoData['pending_reports'] : 0}
          fullValue={mancoData ? mancoData['total_reports'] : 0}
          title={'To Be Processed'}
          chartColor={mainColors.Alert}
          centerText={
            mancoData
              ? percentageToTwoDecimalPlaces(
                  mancoData['pending_reports'] / mancoData['total_reports'],
                )
              : '0.00%'
          }
          startLabel="0%"
          endLabel="100%"
          textBelow={
            mancoData
              ? `${mancoData['pending_reports']}/${mancoData['total_reports']} Reports`
              : ''
          }
        />
        <GenericGaugeChart
          width={width / 6}
          height={getGuageHeight(width)}
          fillValue={mancoData ? mancoData['no_data_reports'] : 0}
          fullValue={mancoData ? mancoData['total_reports'] : 0}
          title={'Awaiting Data'}
          chartColor={mainColors.Fail}
          centerText={
            mancoData
              ? percentageToTwoDecimalPlaces(
                  mancoData['no_data_reports'] / mancoData['total_reports'],
                )
              : '0.00%'
          }
          startLabel="0%"
          endLabel="100%"
          textBelow={
            mancoData
              ? `${mancoData['no_data_reports']}/${mancoData['total_reports']} Reports`
              : ''
          }
        />
      </div>
    </GridItem>
  );
}

export default MancoSummaryContainer;
